/*
 * Shopping OCR semantic parser
 * - Works fully on-device after OCR text is produced.
 * - Parses product name + quantity + unit + existing shopping category.
 * - TR/EN keyword dictionary. No new category is created here; values are
 *   matched against the categories passed from ShoppingPage.
 */

const DEFAULT_CATEGORY = 'Market';
const DEFAULT_UNIT = 'adet';

const UNIT_ALIASES = {
  kg: ['kg', 'kilo', 'kilogram', 'kilograms', 'kgs', 'kğ'],
  lt: ['lt', 'l', 'litre', 'liter', 'liters', 'litres', 'litr', 'ltr'],
  paket: ['paket', 'pkt', 'pack', 'package', 'packet', 'pkg', 'pak', 'paketi'],
  adet: ['adet', 'ad', 'tane', 'piece', 'pieces', 'pcs', 'pc', 'each', 'ea', 'unit', 'units', 'numara', 'no'],
};

const CATEGORY_ALIASES = {
  Market: ['market', 'grocery'],
  Manav: ['manav', 'greengrocer', 'fruit', 'vegetable'],
  Kasap: ['kasap', 'butcher', 'meat'],
  Şarküteri: ['şarküteri', 'sarkuteri', 'deli', 'charcuterie'],
  Fırın: ['fırın', 'firin', 'bakery', 'bread'],
  Temizlik: ['temizlik', 'cleaning'],
  Mutfak: ['mutfak', 'kitchen'],
  İçecek: ['içecek', 'icecek', 'beverages', 'beverage', 'drink', 'drinks'],
  Kahvaltılık: ['kahvaltılık', 'kahvaltilik', 'breakfast'],
  Atıştırmalık: ['atıştırmalık', 'atistirmalik', 'snacks', 'snack'],
  'Kişisel Bakım': ['kişisel bakım', 'kisisel bakim', 'personal care'],
  Bebek: ['bebek', 'baby'],
  'Evcil Hayvan': ['evcil hayvan', 'pet supplies', 'pet'],
  Elektronik: ['elektronik', 'electronics'],
  Eczane: ['eczane', 'pharmacy'],
  Giyim: ['giyim', 'clothing'],
  'Ev İhtiyaçları': ['ev ihtiyaçları', 'ev ihtiyaclari', 'home supplies', 'home'],
  Diğer: ['diğer', 'diger', 'other'],
};

const CATEGORY_KEYWORDS = {
  Manav: [
    'domates','tomato','tomatoes','salkim domates','cherry domates','biber','pepper','peppers','sivri biber','kapya','çarliston','carliston','salatalık','salatalik','cucumber','cucumbers','patates','potato','potatoes','soğan','sogan','onion','onions','sarımsak','sarimsak','garlic','havuç','havuc','carrot','carrots','kabak','zucchini','courgette','patlıcan','patlican','eggplant','aubergine','marul','lettuce','iceberg','göbek','gobek','roka','rocket','arugula','maydanoz','parsley','dereotu','dill','nane','mint','tere','kress','limon','lemon','lime','portakal','orange','mandalina','tangerine','greyfurt','grapefruit','elma','apple','apples','armut','pear','muz','banana','bananas','çilek','cilek','strawberry','strawberries','üzüm','uzum','grape','grapes','karpuz','watermelon','kavun','melon','avokado','avocado','brokoli','broccoli','karnabahar','cauliflower','lahana','cabbage','ıspanak','ispanak','spinach','pırasa','pirasa','leek','mantar','mushroom','mushrooms','bezelye','pea','peas','fasulye','beans','yeşil fasulye','yesil fasulye','barbunya','kiraz','cherry','vişne','visne','erik','plum','kayısı','kayisi','apricot','şeftali','seftali','peach','nektarin','nectarine','nar','pomegranate','incir','fig','hurma','date','dates','kivi','kiwi','ananas','pineapple','mango','fesleğen','feslegen','basil','zencefil','ginger','turp','radish','pancar','beetroot','kereviz','celery','enginar','artichoke','salata','salad'
  ],
  'İçecek': [
    'su','water','maden suyu','soda','sparkling water','kola','cola','coke','pepsi','fanta','gazoz','sprite','ice tea','icetea','fuse tea','lipton','meyve suyu','juice','orange juice','apple juice','limonata','lemonade','ayran','kefir','milkshake','soğuk çay','soguk cay','enerji içeceği','enerji icecegi','energy drink','redbull','red bull','monster','schweppes','tonik','tonic','şalgam','salgam','boza','komposto','smoothie','ice coffee','iced coffee'
  ],
  Kahvaltılık: [
    'süt','sut','milk','yoğurt','yogurt','yoghurt','peynir','cheese','kaşar','kasar','cheddar','beyaz peynir','feta','labne','lor','çökelek','cokelek','tereyağı','tereyagi','butter','kaymak','cream','krema','kefir','yumurta','egg','eggs','zeytin','olive','olives','bal','honey','reçel','recel','jam','marmelat','pekmez','tahin','helva','sucuk','salam','sosis','jambon','pastırma','pastirma','cereal','mısır gevreği','misir gevregi','granola','corn flakes','gevrek','kahvaltılık','breakfast'
  ],
  Atıştırmalık: [
    'bisküvi','biskuvi','biscuit','biscuits','oreo','eti','ülker','ulker','gofret','wafer','kraker','cracker','crackers','cips','chips','patates cipsi','çikolata','cikolata','chocolate','kek','cake','muffin','kurabiye','cookie','cookies','şeker','seker','candy','bonibon','sakız','sakiz','gum','dondurma','ice cream','puding','pudding','jelibon','lokum','marshmallow','bar','protein bar','snack','atıştırmalık','atistirmalik','çekirdek','cekirdek','ayçekirdeği','aycekirdegi','fındık','findik','hazelnut','fıstık','fistik','pistachio','peanut','yer fıstığı','yer fistigi','ceviz','walnut','badem','almond','kaju','cashew','leblebi','chickpea','kuruyemiş','kuruyemis','nuts','draje','popcorn','patlamış mısır','patlamis misir'
  ],
  Fırın: [
    'ekmek','bread','somun','baget','baguette','lavaş','lavas','pide','bazlama','simit','poğaça','pogaca','açma','acma','börek','borek','çörek','corek','tost ekmeği','tost ekmegi','sandviç ekmeği','sandvic ekmegi','sandwich bread','galeta','grissini','hamburger ekmeği','burger bun','tortilla','yufka','kadayıf','kadayif','baklava yufkası','pastry','unlu mamul'
  ],
  Kasap: [
    'et','meat','dana','beef','kuzu','lamb','kıyma','kiyma','mince','minced meat','köfte','kofte','steak','bonfile','antrikot','pirzola','kuşbaşı','kusbasi','tavuk','chicken','but','göğüs','gogus','kanat','wing','hindi','turkey','balık','balik','fish','somon','salmon','ton balığı','ton baligi','tuna','levrek','sea bass','çipura','cipura','alabalık','alabalik','karides','shrimp','sucuk','salam','sosis','jambon','pastırma','pastirma'
  ],
  Şarküteri: [
    'şarküteri','sarkuteri','salam','sosis','sucuk','jambon','pastırma','pastirma','füme','fume','deli','kaşar','kasar','peynir','cheese','zeytin','olive','turşu','tursu','pickle','pickles','mezze','meze','humus','haydari','ezme','salamura'
  ],
  Temizlik: [
    'deterjan','detergent','omo','persil','rinso','abc','alo','yumoş','yumos','softener','yumuşatıcı','yumusatici','çamaşır suyu','camasir suyu','bleach','bulaşık','bulasik','dishwasher','fairy','finish','pril','tablet','parlatıcı','parlatici','kireç çözücü','kirec cozucu','yağ çözücü','yag cozucu','cleaner','temizleyici','cif','domestos','porçöz','porcoz','camsil','glass cleaner','yüzey temizleyici','surface cleaner','sabun','soap','sıvı sabun','sivi sabun','kağıt havlu','kagit havlu','paper towel','tuvalet kağıdı','tuvalet kagidi','toilet paper','peçete','pecete','napkin','ıslak mendil','islak mendil','wet wipes','çöp poşeti','cop poseti','trash bag','garbage bag','sünger','sunger','sponge','bez','cloth','mop','paspas','fırça','firca','brush','eldiven','glove','gloves','dezenfektan','disinfectant'
  ],
  Mutfak: [
    'makarna','pasta','spaghetti','spagetti','pirinç','pirinc','rice','bulgur','nohut','chickpea','mercimek','lentil','fasulye','beans','un','flour','şeker','seker','sugar','tuz','salt','karabiber','black pepper','baharat','spice','kimyon','cumin','nane','mint','pul biber','paprika','kekik','thyme','tarçın','tarcin','cinnamon','yağ','yag','oil','zeytinyağı','zeytinyagi','olive oil','ayçiçek yağı','aycicek yagi','sunflower oil','salça','salca','tomato paste','sirke','vinegar','sos','sauce','ketçap','ketcap','ketchup','mayonez','mayonnaise','hardal','mustard','konserve','canned','ton balığı','tuna','mısır','misir','corn','çorba','corba','soup','maya','yeast','kabartma tozu','baking powder','vanilin','vanilla','nişasta','nisasta','starch','galeta unu','bread crumbs'
  ],
  'Kişisel Bakım': [
    'şampuan','sampuan','shampoo','saç kremi','sac kremi','conditioner','duş jeli','dus jeli','shower gel','diş macunu','dis macunu','toothpaste','diş fırçası','dis fircasi','toothbrush','ağız bakım','agiz bakim','mouthwash','deodorant','roll on','parfüm','parfum','perfume','tıraş','tiras','shave','razor','jilet','pamuk','cotton','kulak çubuğu','kulak cubugu','cotton bud','ped','pad','hijyenik ped','tampon','ıslak mendil','wet wipes','krem','cream','losyon','lotion','güneş kremi','gunes kremi','sunscreen','makyaj','makeup','ruj','lipstick','maskara','mascara','fondöten','fondoten','cilt','skin care','yüz temizleme','face wash','el kremi','hand cream'
  ],
  Bebek: [
    'bebek','baby','bez','diaper','diapers','prima','molfix','sleepy','pampers','mama','formula','devam sütü','devam sutu','baby food','biberon','feeding bottle','emzik','pacifier','ıslak mendil','islak mendil','wet wipes','pişik','pisik','rash cream','bebek şampuanı','bebek sampuani','baby shampoo','bebek yağı','bebek yagi','baby oil','bebek bezi','bebek kremi','baby cream','önlük','onluk','bib','oyuncak','toy','toys','bebek maması','baby cereal'
  ],
  'Evcil Hayvan': [
    'kedi','cat','köpek','kopek','dog','mama','pet food','kedi maması','kedi mamasi','cat food','köpek maması','kopek mamasi','dog food','kum','cat litter','tasma','leash','ödül maması','odul mamasi','treat','pet','kuş yemi','kus yemi','bird food','akvaryum','aquarium','balık yemi','balik yemi','fish food'
  ],
  Eczane: [
    'ilaç','ilac','medicine','drug','pharmacy','eczane','parol','nurofen','calpol','dolven','augmentin','antibiyotik','antibiotic','vitamin','mineral','omega','balık yağı','balik yagi','ateş ölçer','ates olcer','thermometer','bandaj','bandage','yara bandı','yara bandi','plaster','serum','burun spreyi','nasal spray','pastil','lozenge','öksürük','oksuruk','cough','ağrı kesici','agri kesici','painkiller','muayene','medula'
  ],
  Giyim: [
    'tişört','tisort','tshirt','t-shirt','shirt','gömlek','gomlek','pantolon','pants','trousers','jean','kot','elbise','dress','etek','skirt','ceket','jacket','mont','coat','kazak','sweater','çorap','corap','socks','ayakkabı','ayakkabi','shoes','terlik','slippers','iç çamaşırı','ic camasiri','underwear','pijama','pajamas','şapka','sapka','hat','bere','atkı','atki','scarf','eldiven','gloves','kemer','belt','çanta','canta','bag'
  ],
  Elektronik: [
    'pil','battery','batteries','kablo','cable','şarj','sarj','charger','kulaklık','kulaklik','headphones','earphone','usb','adaptör','adaptor','adapter','ampul','bulb','led','priz','socket','mouse','klavye','keyboard','telefon','phone','tablet','powerbank','hafıza kartı','hafiza karti','memory card'
  ],
  'Ev İhtiyaçları': [
    'kalem','pen','pencil','silgi','eraser','defter','notebook','kağıt','kagit','paper','dosya','file','klasör','klasor','folder','bant','tape','yapıştırıcı','yapistirici','glue','makas','scissors','zımba','zimba','stapler','poşet','poset','bag','plastik kasa','askı','aski','hanger','mum','candle','çakmak','cakmak','lighter','pil','battery','ampul','bulb','havlu','towel','çarşaf','carsaf','sheet','yastık','yastik','pillow','örtü','ortu','cover','sepet','basket','kutu','box','saklama kabı','saklama kabi','container','alüminyum folyo','aluminyum folyo','foil','streç','strec','stretch film','buzdolabı poşeti','buzdolabi poseti','freezer bag'
  ],
};

function stripDiacritics(value = '') {
  return String(value)
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function titleCaseTr(value = '') {
  return String(value)
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => {
      if (!word) return '';
      if (/^[A-ZÇĞİÖŞÜ0-9]{2,}$/.test(word) && /\d/.test(word)) return word;
      return word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR');
    })
    .join(' ');
}

function normalizeNumber(value) {
  const n = parseFloat(String(value || '').replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return '1';
  return String(n).replace('.', ',');
}

function canonicalUnit(value) {
  const clean = stripDiacritics(String(value || '').replace(/[.,:;]+$/g, ''));
  for (const [unit, aliases] of Object.entries(UNIT_ALIASES)) {
    if (aliases.some((a) => stripDiacritics(a) === clean)) return unit;
  }
  return '';
}

function cleanProductName(value = '') {
  return titleCaseTr(String(value || '')
    .replace(/^[-–—•·*>✓✗✘□■◦○●✔]\s*/u, '')
    .replace(/^\d+[.)]\s*/, '')
    .replace(/[,:;]+$/g, '')
    .replace(/^[,:;]+/g, '')
    .replace(/\s+/g, ' ')
    .trim());
}

export function detectShoppingCategory(productName, availableCategories = []) {
  const name = stripDiacritics(productName);
  const scores = {};

  for (const [category, words] of Object.entries(CATEGORY_KEYWORDS)) {
    let score = 0;
    for (const word of words) {
      const w = stripDiacritics(word);
      if (!w || w.length < 2) continue;
      if (name === w) score += 16;
      else if (name.includes(w)) score += Math.min(12, Math.max(3, w.length / 2));
    }
    if (score > 0) scores[category] = score;
  }

  const best = Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] || DEFAULT_CATEGORY;
  return normalizeShoppingCategory(best, availableCategories);
}

export function normalizeShoppingCategory(category, availableCategories = []) {
  const list = (availableCategories || []).filter(Boolean).filter((x) => !/^tümü$|^all$/i.test(String(x)));
  if (!list.length) return category || DEFAULT_CATEGORY;

  const target = stripDiacritics(category || DEFAULT_CATEGORY);
  const direct = list.find((item) => stripDiacritics(item) === target);
  if (direct) return direct;

  for (const [canonical, aliases] of Object.entries(CATEGORY_ALIASES)) {
    if (stripDiacritics(canonical) === target || aliases.some((a) => stripDiacritics(a) === target)) {
      const matched = list.find((item) => {
        const clean = stripDiacritics(item);
        return clean === stripDiacritics(canonical) || aliases.some((a) => clean === stripDiacritics(a));
      });
      if (matched) return matched;
    }
  }

  return list.find((item) => stripDiacritics(item) === stripDiacritics(DEFAULT_CATEGORY)) || list[0] || DEFAULT_CATEGORY;
}

export function normalizeShoppingUnit(unit, availableUnits = []) {
  const canonical = canonicalUnit(unit) || DEFAULT_UNIT;
  const list = (availableUnits || []).filter(Boolean);
  if (!list.length) return canonical;
  const direct = list.find((item) => stripDiacritics(item) === stripDiacritics(canonical));
  if (direct) return direct;
  const alias = list.find((item) => canonicalUnit(item) === canonical);
  return alias || canonical;
}

export function parseShoppingLine(rawLine, options = {}) {
  const availableCategories = options.availableCategories || [];
  const availableUnits = options.availableUnits || [];
  let line = String(rawLine || '')
    .replace(/[|]/g, ' ')
    .replace(/[×]/g, 'x')
    .replace(/^[-–—•·*>✓✗✘□■◦○●✔]\s*/u, '')
    .replace(/^\d+[.)]\s*/, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!line || line.length < 2) return null;
  if (/^[\d\s.,₺%*+\-/\\]+$/.test(line)) return null;

  let miktar = '1';
  let birim = DEFAULT_UNIT;
  let name = line;

  const number = String.raw`\d+(?:[.,]\d+)?`;
  const unitWords = Object.values(UNIT_ALIASES).flat().map((u) => u.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');

  // 1 kg Domates, 1 lt Süt, 1 Paket Bisküvi, 2 pcs pen
  let m = name.match(new RegExp(`^(${number})\\s*(${unitWords})\\s+(.+)$`, 'iu'));
  if (m) {
    miktar = normalizeNumber(m[1]);
    birim = canonicalUnit(m[2]) || DEFAULT_UNIT;
    name = m[3];
  } else {
    // Domates 1 kg, milk 2 l, biscuit 1 pack
    m = name.match(new RegExp(`^(.+?)\\s+(${number})\\s*(${unitWords})$`, 'iu'));
    if (m) {
      name = m[1];
      miktar = normalizeNumber(m[2]);
      birim = canonicalUnit(m[3]) || DEFAULT_UNIT;
    } else {
      // 2 Kalem / 1 Silgi -> adet
      m = name.match(new RegExp(`^(${number})\\s+(.+)$`, 'iu'));
      if (m) {
        miktar = normalizeNumber(m[1]);
        name = m[2];
        birim = DEFAULT_UNIT;
      }
    }
  }

  // Birim kelimesi yanlışlıkla ürün adında kaldıysa baştan temizle: "Litre Su", "Kilo Ceviz", "Paket Bisküvi".
  const headUnit = name.match(new RegExp(`^(${unitWords})\\s+(.+)$`, 'iu'));
  if (headUnit) {
    birim = canonicalUnit(headUnit[1]) || birim;
    name = headUnit[2];
  }

  name = cleanProductName(name)
    .replace(/^(Kg|Kilo|Kilogram|Lt|L|Litre|Liter|Paket|Pkt|Adet|Tane)\s+/i, '')
    .trim();

  if (name.length < 2 || name.length > 80) return null;

  const kategori = detectShoppingCategory(name, availableCategories);
  return {
    ad: name,
    miktar,
    birim: normalizeShoppingUnit(birim, availableUnits),
    kategori,
  };
}

export function parseShoppingText(rawText, options = {}) {
  const rawLines = String(rawText || '')
    .split(/\r?\n|[,;|•·]+/u)
    .map((x) => x.trim())
    .filter(Boolean);

  const items = [];
  const seen = new Set();
  for (const line of rawLines) {
    const parsed = parseShoppingLine(line, options);
    if (!parsed) continue;
    const key = stripDiacritics(`${parsed.ad}|${parsed.miktar}|${parsed.birim}|${parsed.kategori}`);
    if (seen.has(key)) continue;
    seen.add(key);
    items.push(parsed);
    if (items.length >= 80) break;
  }
  return items;
}
